import { useMemo, type FC } from "react";
import type { DashboardViewProps } from "../../types/dashboardView";
import { AnalyticChartPanel } from "./analyticShared/AnalyticChartPanel";
import { AnalyticLegend } from "./analyticShared/AnalyticLegend";
import {
  CADENCE_BUCKETS,
  completionBuckets,
  toCompletionTimes,
} from "./analyticShared/completionBuckets";
import { useScopeChildren } from "./analyticShared/scopeChildren";
import { useChartBox } from "./analyticShared/useChartBox";
import { CHART_THEME_COLORS } from "../Common/Chart/chartTheme";
import { BarChart } from "../Common/Chart/BarChart";
import type { ChartResult, ChartSpec } from "../../types/chart";

export const CadenceView: FC<DashboardViewProps> = ({
  elements,
  viewStartMs,
  viewEndMs,
}) => {
  const { parentRef, width, height } = useChartBox();
  const adaptedElements = useMemo(
    () =>
      elements.map((element) => ({
        ...element,
        ...toCompletionTimes(element),
      })),
    [elements],
  );
  const scope = useScopeChildren(adaptedElements);
  const ids = useMemo(
    () => new Map(adaptedElements.map((element) => [element.id, element])),
    [adaptedElements],
  );
  const counts = useMemo(
    () =>
      completionBuckets(scope.children, ids, {
        startMs: viewStartMs,
        endMs: viewEndMs,
      }),
    [scope.children, ids, viewStartMs, viewEndMs],
  );
  const colors = useMemo(
    () =>
      scope.children.map((child, i) =>
        child.ownColor && /^#[0-9a-fA-F]{6}$/.test(child.ownColor)
          ? child.ownColor
          : CHART_THEME_COLORS[i % CHART_THEME_COLORS.length],
      ),
    [scope.children],
  );
  const totals = CADENCE_BUCKETS.map((_, index) =>
    counts.reduce((sum, row) => sum + (row[index] ?? 0), 0),
  );
  const totalN = totals.reduce((sum, value) => sum + value, 0);

  const legend = scope.children.length ? (
    <AnalyticLegend
      children={scope.children}
      colors={colors}
      nextLevelName={scope.nextLevelName}
      onDrillInto={scope.drillInto}
      terminal={scope.nextLevelName === "checklist"}
      metaFor={(child) =>
        scope.nextLevelName === "checklist"
          ? "leaf"
          : `${counts[scope.children.indexOf(child)].reduce((a, b) => a + b, 0)} completions`
      }
    />
  ) : null;
  const caption = (
    <>
      Scope: <strong>{scope.parent?.title ?? "Board"}</strong> · showing{" "}
      <strong>{scope.children.length}</strong> {scope.nextLevelName}s, coloured
      as <em>{scope.nextLevelName}s</em>.
    </>
  );
  if (!totalN)
    return (
      <AnalyticChartPanel
        title="Cadence"
        hint={
          <>
            How long <em>{scope.nextLevelName}s</em> take to close. Long tail
            flagged.
          </>
        }
        scopeCaption={caption}
        legend={legend}
      >
        <div className="analyticEmpty">No completions in this scope yet.</div>
      </AnalyticChartPanel>
    );

  const seriesColorMap: Record<string, string> = {};
  scope.children.forEach((child) => {
    if (child.ownColor && /^#[0-9a-fA-F]{6}$/.test(child.ownColor)) {
      seriesColorMap[child.id] = child.ownColor;
    }
  });

  const chartSpec: ChartSpec = {
    version: 1,
    id: "cadence-chart",
    workspaceId: "views",
    title: "Cadence",
    dataset: "work",
    type: "bar",
    measure: { field: "count", agg: "count" },
    range: { kind: "all" },
    filters: [],
    style: {
      palette: Object.keys(seriesColorMap).length > 0 ? "custom" : "theme",
      seriesColor: Object.keys(seriesColorMap).length > 0 ? seriesColorMap : undefined,
      legend: "off",
      dataLabels: false,
      gridlines: true,
    },
    createdBy: "system",
    createdAt: new Date().toISOString(),
    updatedBy: "system",
    updatedAt: new Date().toISOString(),
  };

  const chartData: ChartResult = {
    asOf: new Date().toISOString(),
    series: scope.children.map((child, childIdx) => ({
      key: child.id,
      label: child.title,
      points: CADENCE_BUCKETS.map((bucket, bIdx) => ({
        x: bucket.label,
        y: counts[childIdx]?.[bIdx] ?? 0,
      })),
    })),
  };

  const dots = totalN < 8;
  return (
    <AnalyticChartPanel
      title="Cadence"
      hint={
        <>
          How long <em>{scope.nextLevelName}s</em> take to close. Long tail
          flagged.
        </>
      }
      scopeCaption={caption}
      morphBadge={dots ? `Dot plot · N=${totalN}` : null}
      legend={legend}
    >
      <div ref={parentRef} style={{ width: "100%" }}>
        <BarChart
          spec={chartSpec}
          data={chartData}
          width={width}
          height={height}
          testId="cadence-barchart"
          onSeriesClick={
            scope.nextLevelName !== "checklist"
              ? (childId) => scope.drillInto(childId)
              : undefined
          }
          renderOverlay={({ xScale, yScale, innerHeight }) => {
            const bandwidth = xScale.bandwidth?.() ?? 40;
            return (
              <g className="cadence-overlay">
                <rect
                  className="target-zone"
                  x={xScale(CADENCE_BUCKETS[0].label) ?? 0}
                  y={0}
                  width={((xScale(CADENCE_BUCKETS[2].label) ?? 0) - (xScale(CADENCE_BUCKETS[0].label) ?? 0)) + bandwidth}
                  height={innerHeight}
                  style={{ fill: 'var(--success)' }}
                  opacity=".08"
                />
                {CADENCE_BUCKETS.map((bucket, b) => {
                  const bx = (xScale(bucket.label) ?? 0) + bandwidth / 2;
                  const total = totals[b];
                  const yPos = yScale(total);
                  return (
                    <g key={bucket.label}>
                      {b >= 3 && total > 0 && (
                        <rect
                          className="long-tail"
                          x={(xScale(bucket.label) ?? 0) - 2}
                          y={yPos - 2}
                          width={bandwidth + 4}
                          height={Math.max(0, innerHeight - yPos + 4)}
                          fill="none"
                          style={{ stroke: 'var(--warning)' }}
                          strokeDasharray="3 2"
                          rx="4"
                        />
                      )}
                      {total > 0 && (
                        <text
                          x={bx}
                          y={yPos - 6}
                          textAnchor="middle"
                          fontSize="11"
                          style={{ fill: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}
                        >
                          {total}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          }}
        />
      </div>
      <style>{`.analyticEmpty{min-height:360px;display:grid;place-items:center;color:var(--text-muted)}`}</style>
    </AnalyticChartPanel>
  );
};
